import { GAME_CONFIG } from '../constants/gameConfig';

interface ProductLaunchSchedule {
  competitorId: string;
  productType: string;
  month: number;
  yearIntroduced?: number; // For products that start in later years
  isAnnual: boolean;
}

interface ScheduledLaunch {
  competitorId: string;
  productType: string;
  productName: string;
  year: number;
  month: number;
  hasLaunched: boolean;
}

export class SeasonalLaunchService {
  private static instance: SeasonalLaunchService;
  private launchSchedules: ProductLaunchSchedule[] = [];
  private scheduledLaunches: ScheduledLaunch[] = [];
  private currentGameYear: number = GAME_CONFIG.STARTING_YEAR;

  private constructor() {
    this.initializeLaunchSchedules();
  }

  public static getInstance(): SeasonalLaunchService {
    if (!SeasonalLaunchService.instance) {
      SeasonalLaunchService.instance = new SeasonalLaunchService();
    }
    return SeasonalLaunchService.instance;
  }

  private initializeLaunchSchedules(): void {
    this.launchSchedules = [
      // Apple - iPhone every September (starting 2007)
      {
        competitorId: 'apple',
        productType: 'iphone',
        month: 9,
        yearIntroduced: 2007,
        isAnnual: true,
      },
      
      // Samsung - Galaxy every February
      {
        competitorId: 'samsung',
        productType: 'galaxy',
        month: 2,
        isAnnual: true,
      },
      
      // Samsung - Galaxy Fold starting 2022 (also February)
      {
        competitorId: 'samsung',
        productType: 'galaxy_fold',
        month: 2,
        yearIntroduced: 2022,
        isAnnual: true,
      },
      
      // Google - Nexus starting 2008 (May)
      {
        competitorId: 'google',
        productType: 'nexus',
        month: 5,
        yearIntroduced: 2008,
        isAnnual: true,
      },
      
      // Google - Pixel starting 2018 (May)
      {
        competitorId: 'google',
        productType: 'pixel',
        month: 5,
        yearIntroduced: 2018,
        isAnnual: true,
      },
      
      // LG - G series every April (until 2021)
      {
        competitorId: 'lg',
        productType: 'g',
        month: 4,
        isAnnual: true,
      },
      
      // Nokia - N series every September
      {
        competitorId: 'nokia',
        productType: 'n',
        month: 9,
        isAnnual: true,
      },
      
      // Palm - Pre series every June (until 2012)
      {
        competitorId: 'palm',
        productType: 'pre',
        month: 6,
        isAnnual: true,
      },
      
      // Motorola - RAZR series every March (until 2017)
      {
        competitorId: 'motorola',
        productType: 'razr',
        month: 3,
        isAnnual: true,
      },
      
      // Microsoft - Windows Phone every October
      {
        competitorId: 'microsoft',
        productType: 'windows_phone',
        month: 10,
        isAnnual: true,
      },
      
      // RIM - BlackBerry series every November (until 2014)
      {
        competitorId: 'rim',
        productType: 'blackberry',
        month: 11,
        isAnnual: true,
      },
    ];
  }

  /**
   * Update the current game year and generate scheduled launches
   */
  public updateGameYear(year: number): void {
    if (year !== this.currentGameYear) {
      this.currentGameYear = year;
      this.generateScheduledLaunches(year);
    }
  }

  /**
   * Generate scheduled launches for a given year
   */
  private generateScheduledLaunches(year: number): void {
    this.launchSchedules.forEach(schedule => {
      // Check if this product should be available this year
      if (schedule.yearIntroduced && year < schedule.yearIntroduced) {
        return; // Product not yet introduced
      }

      // Handle product transitions (Nexus -> Pixel)
      if (schedule.productType === 'nexus' && year >= 2018) {
        return; // Nexus discontinued, replaced by Pixel
      }

      // Check if we already have a scheduled launch for this year/month/competitor/type
      const existingLaunch = this.scheduledLaunches.find(launch => 
        launch.year === year && 
        launch.month === schedule.month && 
        launch.competitorId === schedule.competitorId &&
        launch.productType === schedule.productType
      );

      if (!existingLaunch) {
        const productName = this.generateProductName(schedule.competitorId, schedule.productType, year);
        
        this.scheduledLaunches.push({
          competitorId: schedule.competitorId,
          productType: schedule.productType,
          productName,
          year,
          month: schedule.month,
          hasLaunched: false,
        });
      }
    });
  }

  /**
   * Get scheduled launches for a specific month/year
   */
  public getScheduledLaunches(year: number, month: number): ScheduledLaunch[] {
    return this.scheduledLaunches.filter(launch => 
      launch.year === year && 
      launch.month === month && 
      !launch.hasLaunched
    );
  }

  /**
   * Mark a launch as completed
   */
  public markLaunchCompleted(competitorId: string, year: number, month: number, productType: string): void {
    const launch = this.scheduledLaunches.find(launch => 
      launch.competitorId === competitorId &&
      launch.year === year && 
      launch.month === month &&
      launch.productType === productType
    );
    
    if (launch) {
      launch.hasLaunched = true;
    }
  }

  /**
   * Check if a competitor should launch a product this month
   */
  public shouldLaunchProduct(competitorId: string, year: number, month: number): ScheduledLaunch | null {
    this.updateGameYear(year);
    
    const scheduledLaunches = this.getScheduledLaunches(year, month);
    return scheduledLaunches.find(launch => launch.competitorId === competitorId) || null;
  }

  /**
   * Generate realistic product names based on competitor and type
   */
  private generateProductName(competitorId: string, productType: string, year: number): string {
    const yearsSinceStart = year - GAME_CONFIG.STARTING_YEAR;
    
    switch (competitorId) {
      case 'apple':
        if (productType === 'iphone') {
          // iPhone numbering system
          const yearsSinceEntry = year - 2007;
          if (yearsSinceEntry === 0) {
            return 'iPhone';
          } else {
            return `iPhone ${yearsSinceEntry + 1}`;
          }
        }
        break;
        
      case 'samsung':
        if (productType === 'galaxy') {
          // Galaxy S series numbering
          if (yearsSinceStart === 0) {
            return 'Galaxy S';
          } else {
            return `Galaxy S${yearsSinceStart + 1}`;
          }
        } else if (productType === 'galaxy_fold') {
          // Galaxy Fold numbering
          const yearsSinceFoldStart = year - 2022;
          if (yearsSinceFoldStart === 0) {
            return 'Galaxy Z Fold';
          } else {
            return `Galaxy Z Fold${yearsSinceFoldStart + 1}`;
          }
        }
        break;
        
      case 'google':
        if (productType === 'nexus') {
          // Nexus numbering
          const yearsSinceEntry = year - 2008;
          if (yearsSinceEntry === 0) {
            return 'Nexus One';
          } else {
            return `Nexus ${yearsSinceEntry + 1}`;
          }
        } else if (productType === 'pixel') {
          // Pixel numbering
          const yearsSincePixelStart = year - 2025;
          if (yearsSincePixelStart === 0) {
            return 'Pixel';
          } else {
            return `Pixel ${yearsSincePixelStart + 1}`;
          }
        }
        break;
        
      case 'lg':
        if (productType === 'g') {
          if (yearsSinceStart === 0) {
            return 'G';
          } else {
            return `G${yearsSinceStart + 1}`;
          }
        }
        break;
        
      case 'nokia':
        if (productType === 'n') {
          // N series used actual numbers like N70, N95, etc.
          const baseNumber = 70 + (yearsSinceStart * 5);
          return `N${baseNumber}`;
        }
        break;
        
      case 'palm':
        if (productType === 'pre') {
          if (yearsSinceStart === 0) {
            return 'Pre';
          } else if (yearsSinceStart === 1) {
            return 'Pre Plus';
          } else {
            return `Pre ${yearsSinceStart}`;
          }
        }
        break;
        
      case 'motorola':
        if (productType === 'razr') {
          if (yearsSinceStart === 0) {
            return 'RAZR';
          } else {
            // RAZR V3, V8, V9, etc.
            return `RAZR V${yearsSinceStart + 2}`;
          }
        }
        break;
        
      case 'microsoft':
        if (productType === 'windows_phone') {
          // Windows Phone versioning: 7, 7.5, 8, 8.1, 10
          if (yearsSinceStart === 0) {
            return 'Windows Phone 7';
          } else if (yearsSinceStart === 1) {
            return 'Windows Phone 7.5';
          } else if (yearsSinceStart === 2) {
            return 'Windows Phone 8';
          } else if (yearsSinceStart === 3) {
            return 'Windows Phone 8.1';
          } else {
            return 'Windows Phone 10';
          }
        }
        break;
        
      case 'rim':
        if (productType === 'blackberry') {
          // BlackBerry model numbers
          const models = ['Bold', 'Curve', 'Pearl', 'Storm', 'Torch', 'Z10', 'Q10', 'Passport'];
          const modelIndex = Math.min(yearsSinceStart, models.length - 1);
          return `BlackBerry ${models[modelIndex]}`;
        }
        break;
    }
    
    return `${competitorId} Phone ${year}`;
  }

  /**
   * Get all upcoming launches for display/planning purposes
   */
  public getUpcomingLaunches(currentYear: number, currentMonth: number, lookaheadMonths: number = 12): ScheduledLaunch[] {
    const upcoming: ScheduledLaunch[] = [];
    
    for (let i = 0; i < lookaheadMonths; i++) {
      let checkMonth = currentMonth + i;
      let checkYear = currentYear;
      
      while (checkMonth > 12) {
        checkMonth -= 12;
        checkYear++;
      }
      
      this.updateGameYear(checkYear);
      const monthLaunches = this.getScheduledLaunches(checkYear, checkMonth);
      upcoming.push(...monthLaunches);
    }
    
    return upcoming.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      if (a.month !== b.month) return a.month - b.month;
      return a.competitorId.localeCompare(b.competitorId);
    });
  }

  /**
   * Reset the service (useful for testing)
   */
  public reset(): void {
    // console.log('📅 Seasonal Launch Service: Resetting to initial state');
    
    // Clear all scheduled launches
    this.scheduledLaunches = [];
    
    // Reset to starting year
    this.currentGameYear = GAME_CONFIG.STARTING_YEAR;
    
    // Re-initialize launch schedules to ensure clean state
    this.initializeLaunchSchedules();
    
    // console.log('📅 Seasonal Launch Service: Reset complete');
  }
}

export const seasonalLaunchService = SeasonalLaunchService.getInstance();